import React, { useEffect, useRef, useState, useCallback } from 'react';

// Generic host for any canvas renderer module that exposes the shared
// { setMessages, onSelect, onHover, resize, destroy } interface (see
// fireflies/renderer.js and koi_pond/renderer.js). Adding a new canvas
// template only ever means writing one more renderer module with this same
// shape and registering it in canvases/registry.js — this component and the
// pages that use it never need to change.
export default function CanvasStage({ createStage, messages, backgroundImageUrl, interactive = true }) {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const [tooltip, setTooltip] = useState(null); // { sender_name, message_body, x, y, pinned }
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  const updateContainerSize = useCallback(() => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setContainerSize({ width: rect.width, height: rect.height });
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || typeof createStage !== 'function') return undefined;

    let stage = null;
    try {
      stage = createStage(canvas, { backgroundImageUrl });
    } catch (err) {
      console.error('Canvas stage failed to start', err);
      return undefined;
    }
    stageRef.current = stage;

    if (interactive) {
      stage.onHover?.((hit) => {
        setTooltip((prev) => {
          if (prev?.pinned) return prev;
          return hit ? { ...hit, pinned: false } : null;
        });
      });
      stage.onSelect?.((msg, point) => {
        setTooltip({ ...msg, ...point, pinned: true });
      });
    }

    const ro = new ResizeObserver(() => {
      try { stage.resize?.(); } catch (err) { console.error(err); }
      updateContainerSize();
    });
    if (container) ro.observe(container);

    updateContainerSize();

    return () => {
      ro.disconnect();
      try { stage.destroy?.(); } catch (err) { console.error(err); }
      stageRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createStage, backgroundImageUrl, interactive, updateContainerSize]);

  useEffect(() => {
    stageRef.current?.setMessages(messages || []);
  }, [messages]);

  // Clamp tooltip to stay within canvas bounds
  let tooltipStyle = { left: 0, top: 0 };
  if (tooltip) {
    const tooltipWidth = 260; // approximate tooltip width
    const tooltipHeight = 80; // approximate tooltip height
    const padding = 12;
    const clampedX = Math.max(padding, Math.min(tooltip.x, containerSize.width - tooltipWidth - padding));
    const clampedY = Math.max(padding + tooltipHeight, Math.min(tooltip.y, containerSize.height - padding));
    tooltipStyle = { left: clampedX, top: clampedY };
  }

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%', height: '100%', pointerEvents: interactive ? 'auto' : 'none' }}>
      <canvas ref={canvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      {tooltip && (
        <div
          className="canvas-tooltip"
          style={{
            ...tooltipStyle,
            position: 'absolute',
            transform: 'translate(-50%, -100%)',
            maxWidth: '260px',
            padding: '12px 14px',
            borderRadius: '12px',
            background: 'rgba(15, 18, 30, 0.92)',
            color: '#fff',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.35)',
            pointerEvents: 'auto',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            zIndex: 30,
            fontSize: '0.82rem',
            lineHeight: '1.4',
            wordBreak: 'break-word',
          }}
        >
          {tooltip.pinned && (
            <button
              className="canvas-tooltip-close"
              onClick={() => setTooltip(null)}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: '4px',
                right: '6px',
                background: 'none',
                border: 'none',
                color: 'inherit',
                opacity: 0.6,
                fontSize: '1rem',
                cursor: 'pointer',
                lineHeight: 1,
              }}
            >×</button>
          )}
          <div style={{ fontWeight: 700, fontSize: '0.86rem', marginBottom: '3px' }}>{tooltip.sender_name}</div>
          <div>{tooltip.message_body}</div>
        </div>
      )}
    </div>
  );
}
